import { getActivePage, getActivePageSceneNodes, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import { isComponentInstanceDropParent, isNodeDescendantOf, isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import type { Rect } from "@/lib/math/rect";
import type { LayerDropTarget } from "./LeftPanel";

export function frameDropTargetForMovedNodes(
  document: CurvaDocument,
  nodeIds: string[],
  point: { x: number; y: number },
  activePageId?: string,
): LayerDropTarget | undefined {
  if (nodeIds.length === 0) {
    return undefined;
  }

  const parents = nodeIds.map(nodeId => findDirectParent(document, nodeId));
  if (parents.some(parent => !parent || (parent.type !== "page" && parent.type !== "frame"))) {
    return undefined;
  }

  const movingNodeIds = new Set(nodeIds);
  const targetFrame = topFrameForCanvasDrop(document, point, movingNodeIds, activePageId);
  if (targetFrame) {
    const alreadyInTargetFrame = parents.every(parent => parent?.id === targetFrame.id);
    if (alreadyInTargetFrame) {
      return undefined;
    }

    return {
      parentId: targetFrame.id,
      parentType: "frame",
      index: targetFrame.children.length,
    };
  }

  const page = getActivePage(document, activePageId);
  if (!page) {
    return undefined;
  }

  const needsPageReparent = parents.some(parent => parent?.type === "frame");
  if (!needsPageReparent) {
    return undefined;
  }

  return {
    parentId: page.id,
    parentType: "page",
    index: page.children.length,
  };
}

function topFrameForCanvasDrop(
  document: CurvaDocument,
  point: { x: number; y: number },
  movingNodeIds: Set<string>,
  activePageId?: string,
): Extract<SceneNode, { type: "frame" }> | undefined {
  return [...getActivePageSceneNodes(document, activePageId)]
    .reverse()
    .find((node): node is Extract<SceneNode, { type: "frame" }> => (
      node.type === "frame" &&
      node.visible &&
      !movingNodeIds.has(node.id) &&
      !isNodeLockedForEditing(document, node.id) &&
      !isComponentInstanceDropParent(document, node.id) &&
      ![...movingNodeIds].some(movingNodeId => isNodeDescendantOf(document, node.id, movingNodeId)) &&
      pointInRect(point, node.bounds)
    ));
}

function findDirectParent(document: CurvaDocument, nodeId: string): { id: string; type: LayerDropTarget["parentType"] } | undefined {
  for (const page of document.pages) {
    const parent = findDirectParentInChildren(page.children, nodeId, { id: page.id, type: "page" });
    if (parent) {
      return parent;
    }
  }
  return undefined;
}

function findDirectParentInChildren(
  children: SceneNode[],
  nodeId: string,
  parent: { id: string; type: LayerDropTarget["parentType"] },
): { id: string; type: LayerDropTarget["parentType"] } | undefined {
  for (const node of children) {
    if (node.id === nodeId) {
      return parent;
    }

    const nodeChildren = getSceneNodeChildren(node);
    const parentType = parentTypeForContainer(node);
    if (!nodeChildren || !parentType) {
      continue;
    }

    const nestedParent = findDirectParentInChildren(nodeChildren, nodeId, { id: node.id, type: parentType });
    if (nestedParent) {
      return nestedParent;
    }
  }
  return undefined;
}

function parentTypeForContainer(node: SceneNode): LayerDropTarget["parentType"] | undefined {
  if (node.type === "frame" || node.type === "group" || node.type === "boolean" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    return node.type;
  }
  return undefined;
}

function pointInRect(point: { x: number; y: number }, rect: Rect): boolean {
  return point.x >= rect.x && point.y >= rect.y && point.x <= rect.x + rect.width && point.y <= rect.y + rect.height;
}
